/**
 * Users API
 *
 * Endpoints:
 *   POST   api/v1/credits/apply            - applyCredits
 *   GET    api/v1/credits                  - getCredits
 *   GET    api/v1/users/me                 - getDetails
 *   POST   api/v1/users/me                 - updateDetails
 *   POST   api/v1/users/me/avatar/upload-url - getAvatarUploadUrl
 *   POST   api/v1/users/me/avatar          - confirmAvatar
 *   DELETE api/v1/users/me/avatar          - deleteAvatar
 *   GET    api/v1/users/me/orders          - getOrders, getOrderByExternalId
 *   GET    api/v1/orders/:id               - getOrderDetail
 *   POST   api/v1/orders/:id/pay           - payOrder
 *   GET    api/v1/suborders/:id/json       - getProcessedProductData
 *   GET    api/v1/media/:id                - getProcessedProductMedia
 *   GET    api/v1/users/me/cards           - getSavedCards
 *   DELETE api/v1/users/me/cards/:id       - removeSavedCard
 *   POST   api/v1/users/me/bank-account    - connectBankAccount
 *   DELETE api/v1/users/me/bank-account    - removeBankAccount
 *   POST   api/v1/users/me/providers       - addProvider
 *   DELETE api/v1/users/me/providers       - removeProvider
 *   POST   api/v1/users/delete/request     - requestDeleteAccount
 *   DELETE api/v1/users/delete/confirm     - deleteAccount
 */
import produce from 'utils/produce';
import { formatDateTime } from 'utils/format';
import queryString from 'utils/queryString';
import {
  apiClientGetRequest,
  apiClientPostRequest,
  apiClientDeleteRequest,
} from '../lib/backendRequest';
import { isGiftnoteSku } from '../lib/giftnote';
import { ORDER_STATUS } from './orders';

// Logo files in apps/*/public/static/img/, keyed by lowercased Stripe brand.
// Covers both legacy Sources display names ("American Express") and
// PaymentMethod enum codes ("amex") — see photolane-backend StripeCardSaver.
const CARD_LOGO_BY_BRAND = {
  amex: 'card-american-express.png',
  'american express': 'card-american-express.png',
  diners: 'card-diners-club.png',
  'diners club': 'card-diners-club.png',
  discover: 'card-discover.png',
  jcb: 'card-jcb.png',
  mastercard: 'card-mastercard.png',
  unionpay: 'card-unionpay.png',
  visa: 'card-visa.png',
};

// The customer-facing slice of an order's meta representation (GET /orders/:id
// and the re-pay response). payment_status is settled for every card, Amazon
// and contract order; pending / failed while an ACH debit is with the bank.
const orderDetail = order => ({
  status: order.status,
  paymentStatus: order.payment_status,
  price: order.meta_data.payment_info?.overall,
  achDiscount: order.meta_data.payment_info?.ach_discount ?? 0,
  currency: order.meta_data.payment_info?.currency,
  items: order.meta_data.payment_info?.items ?? [],
  promoCode: order.meta_data.payment_info?.applied_promo_code ?? '',
  shippingAddress: order.meta_data.shipping_address,
});

const usersApi = accessToken => ({
  async applyCredits(countryCode, promoCode) {
    try {
      await apiClientPostRequest(
        'api/v1/credits/apply',
        {
          country_alpha2: countryCode,
          promo_code: promoCode.toLowerCase(),
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      return { data: {} };
    } catch (e) {
      if (e.body.message) {
        return { error: e.body.message };
      }
      return { error: 'Invalid promo code.' };
    }
  },

  /**
   * List the current user's Parabo Credit transactions, newest first.
   * Backend pages with `before_id` (returns `has_next_page` + items).
   *
   * @param {number} [beforeId] paginate older than this transaction id
   * @returns {Promise<{ data?: Array, nextPageBeforeId?: number, error?: any }>}
   */
  async getCredits(beforeId = undefined) {
    try {
      const response = await apiClientGetRequest(
        queryString.stringifyUrl('api/v1/credits', {
          before_id: beforeId,
        }),
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      let nextPageBeforeId;
      if (json.has_next_page && json.data.length > 0) {
        nextPageBeforeId = json.data[json.data.length - 1].id;
      }

      return { data: json.data, nextPageBeforeId };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Returns { userData }
   */
  async getDetails() {
    try {
      const response = await apiClientGetRequest('api/v1/users/me', {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Updates user profile fields (name, email, draft_collab_notifications, etc.)
   *
   * @param {object} fields - fields to update
   */
  async updateDetails(fields) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me',
        fields,
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Issues a presigned S3 POST target for a direct avatar upload
   * (see photolane-backend docs/avatars.md). The grant expires in
   * 15 minutes, max file size is 10 MB.
   *
   * @param {string} contentType image/jpeg | image/png | image/webp
   * @returns {Promise<{ data?: { url: string, fields: object, key: string }, error?: number }>}
   */
  async getAvatarUploadUrl(contentType) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me/avatar/upload-url',
        {
          content_type: contentType,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Uploads the avatar image straight to S3 using the presigned POST from
   * getAvatarUploadUrl. Not a backend endpoint — no auth header, every pair
   * from `fields` goes in unchanged and the file must be the last field.
   *
   * @param {{ url: string, fields: object }} target presigned POST target
   * @param {Blob} file image blob to upload
   */
  async uploadAvatarToS3(target, file) {
    try {
      const formData = new FormData();
      Object.entries(target.fields).forEach(([name, value]) => {
        formData.append(name, value);
      });
      formData.append('file', file);

      const response = await fetch(target.url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        return { error: response.status };
      }

      return { data: true };
    } catch (e) {
      return { error: e.message };
    }
  },

  /**
   * Confirms a finished S3 upload. Processing is asynchronous — the returned
   * user representation still carries the previous avatar_url, so callers
   * should re-fetch getDetails until it changes.
   *
   * @param {string} key S3 key returned by getAvatarUploadUrl
   */
  async confirmAvatar(key) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me/avatar',
        {
          key,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Removes the user's avatar (S3 objects and avatar_url).
   */
  async deleteAvatar() {
    try {
      const response = await apiClientDeleteRequest('api/v1/users/me/avatar', {
        Authorization: `Token ${accessToken}`,
      });

      if (response.ok) {
        return { data: true };
      }
      return { error: response.status };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getOrders(sinceId = undefined) {
    try {
      const response = await apiClientGetRequest(
        queryString.stringifyUrl('api/v1/users/me/orders', {
          since_id: sinceId,
          limit: 25,
        }),
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      let nextPageSinceId;
      if (json.has_next_page && json.data.length > 0) {
        nextPageSinceId = json.data[json.data.length - 1].id;
      }

      const data = json.data.map(order => {
        const { items, shipments, giftnote } = order.sub_orders.reduce(
          (acc, subOrder) => {
            subOrder.items.forEach(item => {
              if (isGiftnoteSku(item.id)) {
                acc.giftnote = item;
              } else {
                acc.items.push(item);
              }
            });
            acc.shipments.push(...subOrder.shipments);
            return acc;
          },
          {
            items: [],
            shipments: [],
            giftnote: null,
          },
        );

        if (giftnote) {
          // add giftnote only one time at the end of the list, with disabled orderable option
          items.push({ ...giftnote, status: 0 });
        }

        return {
          id: order.id,
          externalId: order.external_id,
          status: order.status,
          statusText: ORDER_STATUS[`${order.status}`],
          paymentStatus: order.payment_status,
          createdAt: formatDateTime(order.created_at),
          createdAtRaw: order.created_at,
          // Amount paid; undefined until the backend list representer sends `overall`.
          overall: order.overall,
          currency: order.currency,
          feedbackLeft: order.feedback_left === true,
          items,
          shipments,
        };
      });

      return { data, nextPageSinceId };
    } catch (e) {
      return { error: e.code };
    }
  },

  // Fetches a single order by its external id (e.g. "PL-123456"). The backend
  // route accepts either the numeric primary key or the external id; we send the
  // external id for customer-facing links (post-delivery feedback page).
  async getOrderByExternalId(externalId) {
    try {
      const response = await apiClientGetRequest(`api/v1/orders/${externalId}`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      // Items from `meta_data.payment_info.items` are the ones stored at order
      // creation — each carries `id` (SKU) and (when set by the placing client)
      // a frontend-resolved `name` string.
      const items = json.data.meta_data?.payment_info?.items ?? [];

      return {
        data: {
          id: json.data.id,
          externalId: json.data.external_id,
          feedbackLeft: json.data.feedback_left === true,
          items,
        },
      };
    } catch (e) {
      // 401 = order belongs to a different user. From the customer's
      // perspective the order simply isn't theirs to give feedback on, so we
      // collapse it onto the same not-found state as a missing record.
      if (e.code === 404 || e.code === 401) {
        return { error: 'NOT_FOUND' };
      }
      return { error: e.code };
    }
  },

  async getSkuOrders(productId) {
    const { data, error } = await this.getOrders();

    if (data) {
      const skuOrders = [];
      data.forEach(order => {
        skuOrders.push(...order.items.filter(item => item.id === productId));
      });

      return {
        data: skuOrders,
      };
    }

    return { error };
  },

  async getOrderDetail(orderId) {
    try {
      const response = await apiClientGetRequest(`api/v1/orders/${orderId}`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: orderDetail(json.data) };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Pays a failed bank payment again by card: the bank returned the ACH debit
   * and the order is held until a card covers the stored total without the
   * bank discount (lib/achCheckout repayAmount). Only a failed payment
   * qualifies; a pending one may still settle and a settled one is paid.
   *
   * @param {number} orderId
   * @param {Object} paymentData `stripe` or `saved_card`, as for placing an order.
   * @param {number} expectedPrice
   * @returns {Promise<{ data?: Object, error?: string }>} 400 = nothing to pay any more, 402 = declined.
   */
  async payOrder(orderId, paymentData, expectedPrice) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/orders/${orderId}/pay`,
        { payment_data: { expected_price: expectedPrice, ...paymentData } },
        { Authorization: `Token ${accessToken}` },
      );

      const json = await response.json();

      return { data: orderDetail(json.data) };
    } catch (e) {
      return { error: e.body || 'Oh no! We’re sorry, your payment could not be processed. Please try again.' };
    }
  },

  async getProcessedProductData(processedProductId) {
    try {
      const response = await apiClientGetRequest(`api/v1/suborders/${processedProductId}/json`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProcessedProductMedia(processedProductId, countryCode) {
    try {
      const response = await apiClientGetRequest(
        `api/v1/media/${processedProductId}?location_country_alpha2=${countryCode}`,
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProcessedProductDataWithMedia(processedProductId, countryCode) {
    const { data: productData, error: productDataError } = await this.getProcessedProductData(
      processedProductId,
    );

    if (productDataError) {
      return { error: productDataError };
    }

    const { data: productMedia, error: productMediaError } = await this.getProcessedProductMedia(
      processedProductId,
      countryCode,
    );

    if (productMediaError) {
      return { error: productMediaError };
    }

    return {
      data: produce(productData, draft => {
        const convertPageData = page => {
          if (page.placeholders) {
            return {
              ...page,
              placeholders: page.placeholders.map(placeholder => {
                if (placeholder.type === 'photo' || placeholder.type === 'media') {
                  const mediumId = placeholder.value;
                  const medium = productMedia[mediumId];

                  if (medium) {
                    let photoUrl = medium.url;

                    if (photoUrl) {
                      if (photoUrl.indexOf('X-Amz-Signature=') >= 0) {
                        photoUrl = queryString.parseUrl(photoUrl).url;
                      }
                    }

                    return {
                      ...placeholder,
                      value: photoUrl,
                      mediumId,
                      photo_type: medium.mimeType,
                    };
                  }
                }

                return placeholder;
              }),
            };
          }

          return page;
        };

        if (productData.cover) {
          draft.cover = convertPageData(productData.cover);
        }
        if (productData.pages) {
          draft.pages = productData.pages.map(convertPageData);
        }
      }),
    };
  },

  async getSavedCards() {
    try {
      const response = await apiClientGetRequest('api/v1/users/me/cards', {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      const data = json.data
        .map(card => ({
          id: card.id,
          expired: card.expired,
          default: card.default,
          brand: card.info.brand,
          name: card.info.name,
          expiration: `${card.info.exp_month}/${card.info.exp_year}`,
          last4: card.info.last4,
          number: `**** **** **** ${card.info.last4}`,
          logo: CARD_LOGO_BY_BRAND[card.info.brand.toLowerCase()] ?? null,
        }))
        .sort((a, b) => b.default - a.default);

      return { data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async removeSavedCard(cardId) {
    try {
      // 204 No Content on success, non-2xx responses throw in httpRequest.
      await apiClientDeleteRequest(`api/v1/users/me/cards/${cardId}`, {
        Authorization: `Token ${accessToken}`,
      });

      return { data: true };
    } catch {
      // The backend body is technical ("Card with given ID (…)"), toast a sentence instead.
      return { error: 'Your card could not be removed. Please try again.' };
    }
  },

  /**
   * Save the US bank account of a SetupIntent the user has just confirmed
   * with Stripe (see BankAccountSettings). Only the intent id crosses the
   * wire; the backend reads the account from Stripe and rejects anything
   * that is not a succeeded us_bank_account intent of this customer.
   *
   * @param {string} setupIntentId
   * @returns {Promise<{ data?: object, error?: string }>} data = bank account
   */
  async connectBankAccount(setupIntentId) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me/bank-account',
        {
          setup_intent: setupIntentId,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.body || 'Your bank account could not be saved. Please try again.' };
    }
  },

  /**
   * Disconnect the bank account (detaches the Stripe PaymentMethod too).
   */
  async removeBankAccount() {
    try {
      const response = await apiClientDeleteRequest('api/v1/users/me/bank-account', {
        Authorization: `Token ${accessToken}`,
      });

      if (response.ok) {
        return { data: true };
      }
      return { error: response.status };
    } catch (e) {
      return { error: e.body || 'Your bank account could not be removed. Please try again.' };
    }
  },

  /**
   * Adding new provider to current Account
   *
   * @param {object} provider
   */
  async addProvider(provider) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me/providers',
        {
          provider,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      await response.json();

      return { data: true };
    } catch (e) {
      if (e.code === 400 && e.body === 'Credentials are already used') {
        return {
          error:
            'We’re sorry but the account you are trying to use has already been associated with another account.',
        };
      }
      return { error: e.body };
    }
  },

  /**
   * @param {number} type provider type
   * @param {string} remoteId remote_id
   */
  async removeProvider(type, remoteId) {
    try {
      const response = await apiClientDeleteRequest(
        'api/v1/users/me/providers',
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          provider: type,
          remote_id: remoteId,
        },
      );

      await response.json();

      return { data: true };
    } catch (e) {
      return { error: e.message };
    }
  },

  /**
   * Request delete account - it returns code to confirm deletion
   */
  async requestDeleteAccount() {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/delete/request',
        {},
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.message };
    }
  },

  /**
   * Delete account - it confirms deletion
   */
  async deleteAccount(code) {
    try {
      const response = await apiClientDeleteRequest(
        'api/v1/users/delete/confirm',
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          code,
        }
      );

      if (response.ok) {
        return { data: true };
      }
      return { error: response.status };
    } catch (e) {
      return { error: e.message };
    }
  },
});

export default usersApi;
